import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import type { ExtractionResult, Product, ProductCategory } from '../../../types/api';

interface ProductInformationPanelProps {
  extraction: ExtractionResult;
  categories: ProductCategory[];
  products: Product[];
  productsLoading: boolean;
  categoryId: number | '';
  onCategoryChange: (categoryId: number | '') => void;
  selectedProductIds: Set<number>;
  onToggleProduct: (productId: number) => void;
}

/**
 * First panel of the review step: the "AI Generated Information" banner, the
 * "Product Information" tab and the editable extracted values (screens RFP 04/06).
 */
export function ProductInformationPanel({
  extraction,
  categories,
  products,
  productsLoading,
  categoryId,
  onCategoryChange,
  selectedProductIds,
  onToggleProduct,
}: ProductInformationPanelProps) {
  return (
    <section className="panel">
      <div className="review-section">
        <div className="section-banner">AI Generated Information</div>

        <div className="review-section__body">
          <div className="tabs" role="tablist">
            <button type="button" role="tab" className="tabs__item" aria-selected="true">
              Product Information
            </button>
          </div>

          {extraction.summary ? (
            <div className="review-summary">
              <div className="review-summary__label">RFP Summary</div>
              <p className="review-summary__text">{extraction.summary}</p>
            </div>
          ) : null}

          <div className="review-grid review-grid--spaced">
            <label className="review-grid__label" htmlFor="rfp-category">
              Product Category
            </label>
            <div className="review-grid__value">
              <Select
                id="rfp-category"
                aria-label="Product category"
                value={categoryId === '' ? '' : categoryId}
                onChange={(event) =>
                  onCategoryChange(event.target.value === '' ? '' : Number(event.target.value))
                }
                options={[
                  { value: '', label: 'Select a category' },
                  ...categories.map((category) => ({ value: category.id, label: category.name })),
                ]}
                wrapClassName="review-grid__select"
              />
            </div>

            <span className="review-grid__label">Products</span>
            <div className="review-grid__value">
              {productsLoading ? (
                <span className="review-empty">Loading products…</span>
              ) : products.length === 0 ? (
                <span className="review-empty">
                  No products are configured for this category yet.
                </span>
              ) : (
                <div className="product-picker">
                  {products.map((product) => (
                    <label key={product.id} className="product-picker__item">
                      <Checkbox
                        label={product.name}
                        checked={selectedProductIds.has(product.id)}
                        onChange={() => onToggleProduct(product.id)}
                      />
                      <span>{product.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <span className="review-grid__label">Quantity</span>
            <div className="review-grid__value">{extraction.productInformation.quantity}</div>

            <span className="review-grid__label">Information</span>
            <div className="review-grid__value">
              <p className="review-empty review-empty--notes">
                {extraction.productInformation.notes}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
